import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, Col, Row } from 'react-bootstrap';
import { Button, Container, FormGroup, Label } from 'reactstrap';
import { Breadcrumbs, Typography, IconButton, InputBase, Paper, Box, Tabs, Tab } from "@mui/material";
import PaginationTableWithFuncBtn from '../../../components/Util/PaginationTableWithFuncBtn';
import CloudConfigKeyAddModal from './CloudConfigKeyAddModal';
import CloudConfigKeyEditModal from './CloudConfigKeyEditModal';
import DeleteModal from '../../../components/Util/DeleteModal';
import ProjectSelector from '../../../components/Common/ProjectSelector';
import '../../../../src/style/css/_project.css'
import moment from 'moment';
import toastAlert from '../../../js/Base/ToastAlert';
import AddIcon from "@mui/icons-material/Add";
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SearchIcon from '@mui/icons-material/Search';
import { useSelector, useDispatch } from "react-redux";
import { fetchCompanyLicense } from '../../../store/slices/licenseSlice';
import { cloudNativeEnabledSelector, crowdstrikeEnabledSelector } from "../../../store/selectors/licenseSelectors";
import { getConfigKeysByProjectId, deleteConfigKey, setPrimaryConfigKeyApi } from '../../../api/keyApi';

const GCP_KEY_NAME = 'GCP';
const AWS_KEY_NAME = 'AWS';

/** 可綁多把 key 的雲別，列表需要顯示範圍並提供「設為預設」 */
const MULTI_KEY_CLOUDS = [GCP_KEY_NAME, AWS_KEY_NAME];

const normalizeKeyName = (configKey) => String(configKey?.key_name || '').trim().toUpperCase();

const isMultiKeyCloud = (configKey) => MULTI_KEY_CLOUDS.includes(normalizeKeyName(configKey));

/**
 * 一個專案可綁多把 GCP Service Account Key / AWS Account Key，
 * 列表需要能一眼分辨是哪個 GCP 專案／AWS 帳戶
 */
const describeConfigKeyScope = (configKey) => {
  const keyName = normalizeKeyName(configKey);
  const contents = configKey?.key_contents_json || {};
  const primaryMark = configKey?.is_primary ? '（預設）' : '';

  if (keyName === GCP_KEY_NAME) {
    const gcpProjectId = configKey?.gcp_project_id || contents.PROJECT_ID || '未知專案';
    const clientEmail = contents.CLIENT_EMAIL || '';
    return clientEmail
      ? `${gcpProjectId}${primaryMark} / ${clientEmail}`
      : `${gcpProjectId}${primaryMark}`;
  }

  if (keyName === AWS_KEY_NAME) {
    // aws_account_id 由後端以 STS 解析後回填，舊資料在首次讀取前可能還是空的
    const awsAccountId = configKey?.aws_account_id || '帳戶待識別';
    const region = contents.REGION || '';
    return region
      ? `${awsAccountId}${primaryMark} / ${region}`
      : `${awsAccountId}${primaryMark}`;
  }

  return '-';
};

export default function CloudConfigKey() {
  const [configKeys, setConfigKeys] = useState([]);
  const [search, setSearch] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectConfigKey, setselectConfigKey] = useState(null);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const userName = localStorage.getItem("user_email");
  const dispatch = useDispatch();
  // const isAdmin = useSelector(state => state.user.isAdmin);

  // 編輯
  const handleEdit = (id) => {
    const key = configKeys.find((key) => (key.uuid || key.id) === id) || null;
    setselectConfigKey(key);
    setIsEditModalOpen(true);
  };
  
  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    setselectConfigKey(null);
  };
  

  // 新增
  const toggleModal = (id = null) => {
    const key = configKeys.find((key) => key.id === id) || null;
    setselectConfigKey(key);
    setIsModalOpen(true);
  };

  const handleAddModalClose = () => {
    setIsModalOpen(false);
    setselectConfigKey(null);
  };

  const handleSearchChange = (e) => setSearch(e.target.value);

  const handleDelete = (id) => {
    const key = configKeys.find((k) => (k.uuid || k.id) === id);
    setDeleteTarget(key);
    setDeleteModalOpen(true);
  };

  const handleDeleteModalClose = () => {
    setDeleteModalOpen(false);
    setDeleteTarget(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      const payload = {
        config_key_uuid: deleteTarget.uuid || deleteTarget.id,
        key_name: deleteTarget.key_name,
        project_no: deleteTarget.project_no,
        project_uuid: localStorage.getItem('project_uuid'),
        status: 1,
        source: deleteTarget.source,
        acct_email: userName,
      };
      const data = await deleteConfigKey(payload);
      if (data.success) {
        toastAlert.autoClose(true).showSuccess('刪除成功');
        handleDeleteModalClose();
        fetchConfigKeys();
      } else {
        toastAlert.autoClose(true).showError(data.msg || '刪除失敗，請稍後再試');
      }
    } catch (error) {
      if (error.__handled) return;
      toastAlert.autoClose(true).showError('發生錯誤，請稍後再試');
    }
  };

  const handleSetPrimary = async (id) => {
    const key = configKeys.find((k) => (k.uuid || k.id) === id);
    if (!key) return;

    try {
      const data = await setPrimaryConfigKeyApi({
        config_key_uuid: key.uuid || key.id,
        project_uuid: localStorage.getItem('project_uuid'),
        acct_email: userName,
      });
      if (data.success) {
        toastAlert.autoClose(true).showSuccess('已設定為預設憑證');
        fetchConfigKeys();
      } else {
        toastAlert.autoClose(true).showError(data.msg || '設定失敗，請稍後再試');
      }
    } catch (error) {
      if (error.__handled) return;
      toastAlert.autoClose(true).showError('發生錯誤，請稍後再試');
    }
  };

  useEffect(() => {
    dispatch(fetchCompanyLicense());
    fetchConfigKeys();
  }, []);

  const fetchConfigKeys = async () => {
    const projectUuid = localStorage.getItem("project_uuid");
    const userName = localStorage.getItem("user_email");
    try {
      const data = await getConfigKeysByProjectId(projectUuid, userName);
      if (data.success) {
        setConfigKeys(data.keys || []);
      } else {
        toastAlert.autoClose(true).showError('發生錯誤，請稍後再試');
        setConfigKeys([]);
      }
    } catch (error) {
      if (error.__handled) return;
      toastAlert.autoClose(true).showError('發生錯誤，請稍後再試');
    }
  };

  const cloudNativeEnabled = useSelector(cloudNativeEnabledSelector);
  const crowdstrikeEnabled = useSelector(crowdstrikeEnabledSelector);

  const getDefaultTab = () => {
    if (cloudNativeEnabled) return "Cloud Native";
    if (crowdstrikeEnabled) return "CrowdStrike";
    return "Setting";
  };
  
  const [tabIndex, setTabIndex] = useState(getDefaultTab);
  useEffect(() => {
    setTabIndex(getDefaultTab());
  }, [cloudNativeEnabled, crowdstrikeEnabled]);
  
  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const col_name = ['KeyName', 'KeyContents', '到期日', '備註', '動作'];

  const data_frame = [
    { keyName: 'key_name', isBadge: false },
    { keyName: 'key_contents_json', isBadge: false },
    { keyName: 'expiry_date', isBadge: false },
    { keyName: 'remarks', isBadge: false },
    { keyName: 'func', isClick: true, editBtn: true, delBtn: true },
  ];
  const col_size = ['15%', '30%', '15%', '20%', '20%'];

  // 雲原生分頁可能有多把 GCP / AWS key，額外顯示各自的範圍與預設切換
  const cloud_native_col_name = ['KeyName', '雲端範圍', 'KeyContents', '到期日', '備註', '動作'];

  const cloud_native_data_frame = [
    { keyName: 'key_name', isBadge: false },
    { keyName: 'scope_label', isBadge: false },
    { keyName: 'key_contents_json', isBadge: false },
    { keyName: 'expiry_date', isBadge: false },
    { keyName: 'remarks', isBadge: false },
    { keyName: 'func', isClick: true, editBtn: true, delBtn: true, extraBtn: true },
  ];
  const cloud_native_col_size = ['12%', '22%', '22%', '13%', '14%', '17%'];

  const config_keys_by_project_no = configKeys.map((data) => ({
    ...data,
    scope_label: describeConfigKeyScope(data),
    expiry_date: !data.expiry_date ? '' : moment.utc(data.expiry_date).local().format('YYYY-MM-DD HH:mm:ss'),
    key_contents_json: typeof data.key_contents_json === 'object'
      ? JSON.stringify(data.key_contents_json)
      : data.key_contents_json
  }));

  return (
    <React.Fragment>
      <Container className="mt-2">
        <div style={{ marginRight: '20px' }}>
          <Row className="mb-2">
            <Col md={9}>
              <Breadcrumbs
                className="link-area"
                style={{ padding: "0 0 20px 0", fontWeight: 'bold'}}
                separator={<NavigateNextIcon style={{ fontSize: '20px', fontWeight: 'bold' }} />}
                aria-label="breadcrumb"
              >
                <Link to="/admin-management" style={{ color: '#1d1d1d', fontSize: '20px', textDecoration: 'none', fontWeight: 'bold' }}>
                  <p style={{ marginBottom: '0px' }}>網站管理員設定</p>
                </Link>
                <Link to="/project/cloud-config-key" style={{ color: '#1d1d1d', fontSize: '20px', textDecoration: 'none', fontWeight: 'bold' }}>
                  <p style={{ marginBottom: '0px' }}>ConfigKey</p>
                </Link>
              </Breadcrumbs>
            </Col>
            <Col md={3} style={{ justifyContent: 'right', alignContent: 'right' }}>
              <FormGroup>
                <Label style={{ fontSize: '12px', color: '#1D1D1D' }}>
                  <strong>當前專案</strong>
                </Label>
                <ProjectSelector
                  projectSelect={() => {}}
                  disabled={true}
                  style={{ fontSize: '12px', color: '#1d1d1d' }}
                />
              </FormGroup>
            </Col>
          </Row>

          <>
            <div className="ProjectManagement">
              <Tabs
                value={tabIndex}
                onChange={handleTabChange}
                className="provider-tabs"
                sx={{
                  mb: 2,
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontFamily: 'Noto Sans, sans-serif',
                  }
                }}
              >
                {cloudNativeEnabled && <Tab value="Cloud Native" label="雲原生" />}
                {crowdstrikeEnabled && <Tab value="CrowdStrike" label="CrowdStrike" />}
                <Tab value={"Setting"} label="Setting" />
              </Tabs>
            </div>
            <div>
              <Card>
                <Card.Header style={{ backgroundColor: "transparent" }}>
                  <strong>ConfigKey</strong>
                  {config_keys_by_project_no.length > 0 && (
                    <IconButton
                      onClick={() => toggleModal(null)}
                        sx={{
                          color: "black",
                          width: 32,
                          height: 32,
                        }}
                      >
                        <Typography variant="h6" component="span" sx={{ lineHeight: 1 }}>
                          <AddIcon fontSize="medium" />
                        </Typography>
                    </IconButton>
                  )}
                </Card.Header>
                <Card.Body style={{ paddingTop: "5px" }}>

                  {cloudNativeEnabled && (
                    <Box sx={{ display: tabIndex === "Cloud Native" ? "block" : "none" }}>
                      {config_keys_by_project_no.filter(k => k.source === "Cloud Native").length > 0 ? (
                        <>
                          <Row className="mb-3 mt-3">
                            <Col md={8}></Col>
                            <Col md={4}>
                              <Paper variant="outlined" component="form" sx={{ p: '2px 2px', display: 'flex', alignItems: 'left', height: '32px' }}>
                                <InputBase
                                  sx={{ flex: 1, p: '10px' }}
                                  style={{ fontSize: '12px', boxShadow: 'none' }}
                                  placeholder="請輸入搜尋字串"
                                  inputProps={{ 'aria-label': 'search keyword' }}
                                  value={search}
                                  onChange={handleSearchChange}
                                />
                                <IconButton type="button" sx={{ p: '12px' }} aria-label="search">
                                  <SearchIcon style={{ color: '#E2E2E2' }} />
                                </IconButton>
                              </Paper>
                            </Col>
                          </Row>
                          <PaginationTableWithFuncBtn
                            rowData={config_keys_by_project_no.filter(
                              (m) =>
                                m.source === "Cloud Native" &&
                                (
                                  m.key_name.indexOf(search) > -1 ||
                                  m.scope_label.indexOf(search) > -1 ||
                                  m.expiry_date.indexOf(search) > -1 ||
                                  m.key_contents_json.indexOf(search) > -1 ||
                                  m.remarks.indexOf(search) > -1
                                )
                            )}
                            colName={cloud_native_col_name}
                            dataFrame={cloud_native_data_frame}
                            col_size={cloud_native_col_size}
                            editModalOpen={handleEdit}
                            delFunc={handleDelete}
                            extraFunc={handleSetPrimary}
                            extraBtnLabel="設為預設"
                            isExtraBtnVisible={(row) => isMultiKeyCloud(row) && !row.is_primary}
                          />
                        </>
                      ) : (
                        <Col md={12} className="d-flex justify-content-end align-items-center mt-2 mb-2">
                          <Button
                            style={{ width: '90px', height: '34px', fontSize: '14px', fontWeight: 'bold' }}
                            outline
                            onClick={() => toggleModal(null)}
                          >
                            新增
                          </Button>
                        </Col>
                      )}
                    </Box>
                  )}

                  {crowdstrikeEnabled && (
                    <Box sx={{ display: tabIndex === "CrowdStrike" ? "block" : "none" }}>
                      {config_keys_by_project_no.filter(k => k.source === "CrowdStrike").length > 0 ? (
                        <>
                        <Row className="mb-3 mt-3">
                          <Col md={8}></Col>
                          <Col md={4}>
                            <Paper variant="outlined" component="form" sx={{ p: '2px 2px', display: 'flex', alignItems: 'left', height: '32px' }}>
                              <InputBase
                                sx={{ flex: 1, p: '10px' }}
                                style={{ fontSize: '12px', boxShadow: 'none' }}
                                placeholder="請輸入搜尋字串"
                                inputProps={{ 'aria-label': 'search keyword' }}
                                value={search}
                                onChange={handleSearchChange}
                              />
                              <IconButton type="button" sx={{ p: '12px' }} aria-label="search">
                                <SearchIcon style={{ color: '#E2E2E2' }} />
                              </IconButton>
                            </Paper>
                          </Col>
                        </Row>
                        <PaginationTableWithFuncBtn
                          rowData={config_keys_by_project_no.filter(
                            (m) =>
                              m.source === "CrowdStrike" &&
                              (
                                m.key_name.indexOf(search) > -1 ||
                                m.expiry_date.indexOf(search) > -1 ||
                                m.key_contents_json.indexOf(search) > -1 ||
                                m.remarks.indexOf(search) > -1
                              )
                            )}
                          colName={col_name}
                          dataFrame={data_frame}
                          col_size={col_size}
                          editModalOpen={handleEdit}
                          delFunc={handleDelete}
                        />
                        </>
                      ) : (
                        <Col md={12} className="d-flex justify-content-end align-items-center mt-2 mb-2">
                          <Button
                            style={{ width: '90px', height: '34px', fontSize: '14px', fontWeight: 'bold' }}
                            outline
                            onClick={() => toggleModal(null)}
                          >
                            新增
                          </Button>
                        </Col>
                      )}
                    </Box>
                  )}

                  {/* === Setting Key === */}
                  <Box sx={{ display: tabIndex === "Setting" ? "block" : "none" }}>
                    {config_keys_by_project_no.filter(k => k.source === "Setting").length > 0 ? (
                      <>
                      <Row className="mb-3 mt-3">
                        <Col md={8}></Col>
                        <Col md={4}>
                          <Paper variant="outlined" component="form" sx={{ p: '2px 2px', display: 'flex', alignItems: 'left', height: '32px' }}>
                            <InputBase
                              sx={{ flex: 1, p: '10px' }}
                              style={{ fontSize: '12px', boxShadow: 'none' }}
                              placeholder="請輸入搜尋字串"
                              inputProps={{ 'aria-label': 'search keyword' }}
                              value={search}
                              onChange={handleSearchChange}
                            />
                            <IconButton type="button" sx={{ p: '12px' }} aria-label="search">
                              <SearchIcon style={{ color: '#E2E2E2' }} />
                            </IconButton>
                          </Paper>
                        </Col>
                      </Row>
                      <PaginationTableWithFuncBtn
                        rowData={config_keys_by_project_no.filter(
                          (m) =>
                            m.source === "Setting" &&
                            (
                              m.key_name.indexOf(search) > -1 ||
                              m.expiry_date.indexOf(search) > -1 ||
                              m.key_contents_json.indexOf(search) > -1 ||
                              m.remarks.indexOf(search) > -1
                            )
                          )}
                        colName={col_name}
                        dataFrame={data_frame}
                        col_size={col_size}
                        editModalOpen={handleEdit}
                        delFunc={handleDelete}
                      />
                      </>
                    ) : (
                      <Col md={12} className="d-flex justify-content-end align-items-center mt-2 mb-2">
                        <Button
                          style={{ width: '90px', height: '34px', fontSize: '14px', fontWeight: 'bold' }}
                          outline
                          onClick={() => toggleModal(null)}
                        >
                          新增
                        </Button>
                      </Col>
                    )}
                  </Box>
                </Card.Body>

                <CloudConfigKeyAddModal
                  isModalOpen={isModalOpen}
                  handleClose={handleAddModalClose}
                  configKey={selectConfigKey}
                  isCreate={selectConfigKey === null}
                  submitEffect={fetchConfigKeys}
                  source={tabIndex}
                  existingConfigKeys={configKeys}
                />

                {isEditModalOpen && (
                  <CloudConfigKeyEditModal 
                    isModalOpen={isEditModalOpen}
                    handleClose={handleEditModalClose}
                    configKey={selectConfigKey}
                    isCreate={!selectConfigKey}
                    submitEffect={fetchConfigKeys}
                    source={tabIndex}
                  />
                )}

                <DeleteModal
                  isModalOpen={deleteModalOpen}
                  handleClose={handleDeleteModalClose}
                  handleUpdate={handleDeleteConfirm}
                  title={'刪除Config Key'}
                  content={
                    isMultiKeyCloud(deleteTarget)
                      ? `請確認是否刪除 Key：${deleteTarget?.key_name || ''}（${describeConfigKeyScope(deleteTarget)}）`
                      : `請確認是否刪除 Key：${deleteTarget?.key_name || ''}`
                  }
                />
              </Card>
            </div>
          </>

        </div>
      </Container>
    </React.Fragment>
  );
}
